import { type FC, useCallback, useMemo, useRef } from "react";

import { Button } from "@/shared/components/ui/button";
import { Card, CardContent } from "@/shared/components/ui/card";
import CircularProgress from "@/shared/components/CircularProgress";
import { useEstateActivityLog } from "@/shared/hooks/useEstateActivityLog";
import { formatDate, formatRelativeTime, formatTime } from "@/shared/utils/formatters";
import { getActivityIcon, renderActivityRow } from "@/shared/utils/activityLogDisplay";
import { isUnreadForLawyer, type ActivityLogEntry } from "@/shared/types/activityLog";

interface DocumentClickPayload {
  id: number;
  name?: string;
  file_name: string | null;
  created_at: string;
}

interface ActivityLogTabProps {
  estateId: number | undefined;
  seenAt?: string | null;
  onDocumentClick?: (doc: DocumentClickPayload) => void;
}

export const ActivityLogTab: FC<ActivityLogTabProps> = ({
  estateId,
  seenAt = null,
  onDocumentClick,
}) => {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useEstateActivityLog(estateId);

  const entries = useMemo(() => data?.pages.flatMap((page) => page.entries) ?? [], [data]);

  // Freeze the seen-timestamp once it first becomes available so rows don't lose
  // their unread dot while the lawyer is looking at the tab (visiting the tab
  // marks activity as seen). We capture the first non-null value rather than the
  // initial render value, which may be null while the user data is still loading.
  const frozenSeenAt = useRef<string | null>(null);
  if (frozenSeenAt.current === null && seenAt !== null) {
    frozenSeenAt.current = seenAt;
  }

  const handleDocumentClick = useCallback(
    (entry: ActivityLogEntry) => {
      if (!onDocumentClick) return;
      const documentId = entry.entity_id ? Number(entry.entity_id) : NaN;
      if (!Number.isFinite(documentId)) return;
      const meta = entry.metadata as {
        display_name?: unknown;
        file_name?: unknown;
      };
      const displayName =
        typeof meta.display_name === "string" && meta.display_name ? meta.display_name : null;
      const fileName = typeof meta.file_name === "string" && meta.file_name ? meta.file_name : null;
      onDocumentClick({
        id: documentId,
        name: displayName ?? fileName ?? "Document",
        file_name: fileName,
        created_at: entry.created_at,
      });
    },
    [onDocumentClick],
  );

  return (
    <Card>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex justify-center py-8">
            <CircularProgress />
          </div>
        ) : !entries || entries.length === 0 ? (
          <p className="text-center text-secondary py-8">No recent activity to show.</p>
        ) : (
          <div>
            <div className="flex items-center gap-4 px-6 py-3 border-b border-stone-200">
              <div className="flex-1 text-sm font-medium text-secondary uppercase tracking-wider">
                Activity
              </div>
              <div className="text-sm font-medium text-secondary uppercase tracking-wider text-right flex-shrink-0">
                Time
              </div>
            </div>
            <div className="divide-y divide-stone-200">
              {entries.map((entry) => {
                const { Icon, bg, fg } = getActivityIcon(entry);
                const { title, subtitle } = renderActivityRow(entry, {
                  onDocumentClick: onDocumentClick ? handleDocumentClick : undefined,
                });

                const isUnread = isUnreadForLawyer(entry, frozenSeenAt.current);

                return (
                  <div key={entry.id} className="flex items-center gap-4 px-6 py-4">
                    {isUnread && (
                      <span
                        className="h-2 w-2 rounded-full flex-shrink-0 bg-brand"
                        aria-label="Unread"
                      />
                    )}
                    <div
                      className={`w-9 h-9 rounded-full ${bg} flex items-center justify-center flex-shrink-0`}
                    >
                      <Icon className={`w-4 h-4 ${fg}`} aria-hidden />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-secondary truncate">{title}</div>
                      {subtitle && (
                        <div className="text-sm text-secondary truncate mt-0.5">{subtitle}</div>
                      )}
                    </div>
                    <div className="text-right flex-shrink-0 whitespace-nowrap">
                      <div className="text-primary">
                        {formatDate(entry.created_at)} at {formatTime(entry.created_at)}
                      </div>
                      <div className="text-2xs text-tertiary mt-0.5">
                        {formatRelativeTime(entry.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {hasNextPage && (
              <div className="flex justify-center px-6 py-4 border-t border-stone-200">
                <Button
                  variant="outline"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {isFetchingNextPage ? "Loading..." : "Load more"}
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
